import React, { useEffect, useRef } from 'react';
import katex from 'katex';

/**
 * NotePreview - Renders note content with LaTeX equations
 *
 * Parses HTML content and renders any $$...$$ LaTeX blocks using KaTeX
 */
export default function NotePreview({ content, facts, steps, image, imagePosition = 'before' }) {
  const notesRef = useRef(null);
  const factsRef = useRef(null);
  const stepsRefs = useRef([]);

  // Function to render LaTeX in a container
  const renderLatex = container => {
    if (!container) return;

    // Find all text nodes and replace $$...$$ with rendered KaTeX
    const html = container.innerHTML;

    // Match $$...$$ patterns (display math)
    const rendered = html.replace(/\$\$(.*?)\$\$/g, (match, latex) => {
      try {
        return katex.renderToString(latex, {
          displayMode: false,
          throwOnError: false,
          output: 'html',
        });
      } catch (e) {
        console.error('KaTeX error:', e);
        return `<span class="katex-error">${latex}</span>`;
      }
    });

    container.innerHTML = rendered;
  };

  useEffect(() => {
    renderLatex(notesRef.current);
  }, [content]);

  useEffect(() => {
    renderLatex(factsRef.current);
  }, [facts]);

  useEffect(() => {
    stepsRefs.current.forEach(ref => renderLatex(ref));
  }, [steps]);

  const hasImage = typeof image === 'string' && image.startsWith('data:image');
  const imageEl = hasImage && (
    <div className="preview-section preview-image-section">
      <img className="preview-image" src={image} alt="Attachment preview" />
    </div>
  );

  const hasContent = content?.replace(/<[^>]*>/g, '').trim();
  const hasFacts = facts?.replace(/<[^>]*>/g, '').trim();
  const hasSteps = steps?.length > 0 && steps.some(s => s.title?.trim() || s.content?.trim());

  if (!hasContent && !hasFacts && !hasSteps && !hasImage) {
    return (
      <div className="note-preview note-preview-empty">
        <div className="preview-placeholder">
          <span className="preview-icon">👁️</span>
          <p>Preview will appear here as you type...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="note-preview">
      <div className="preview-header">
        <span className="preview-icon">👁️</span>
        <span>Preview</span>
      </div>

      <div className="preview-content">
        {imagePosition === 'before' && imageEl}
        {hasContent && (
          <div className="preview-section">
            <div
              ref={notesRef}
              className="preview-notes"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        )}

        {imagePosition === 'after' && imageEl}

        {hasFacts && (
          <div className="preview-section preview-facts-section">
            <div className="preview-section-label">Facts</div>
            <div
              ref={factsRef}
              className="preview-facts"
              dangerouslySetInnerHTML={{ __html: facts }}
            />
          </div>
        )}

        {hasSteps && (
          <div className="preview-section preview-steps-section">
            <div className="preview-section-label">Steps</div>
            <div className="preview-steps">
              {steps
                .filter(s => s.title?.trim() || s.content?.trim())
                .map((step, index) => (
                  <div key={index} className="preview-step">
                    <div className="preview-step-header">
                      <span className="preview-step-number">{step.step_number}</span>
                      {step.title && <span className="preview-step-title">{step.title}</span>}
                    </div>
                    {step.content && (
                      <div
                        ref={el => (stepsRefs.current[index] = el)}
                        className="preview-step-content"
                        dangerouslySetInnerHTML={{ __html: step.content }}
                      />
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
